import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useAirline } from '../contexts/AirlineContext';

export interface OrgLookupValue {
  id: string;
  org_id: string;
  category: string;
  value: string;
  label: string | null;
  metadata: Record<string, unknown>;
  sort_order: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
}

export function useOrgLookup(category: string) {
  const { user } = useAuth();
  const { selectedAirline } = useAirline();

  const searchValues = useCallback(
    async (query: string): Promise<OrgLookupValue[]> => {
      if (!selectedAirline?.airline_id) return [];

      const { data, error } = await supabase
        .from('org_lookup_values')
        .select('*')
        .eq('org_id', selectedAirline.airline_id)
        .eq('category', category)
        .eq('is_active', true)
        .or(`value.ilike.%${query}%,label.ilike.%${query}%`)
        .order('sort_order', { ascending: true })
        .order('value', { ascending: true });

      if (error) throw error;
      return (data ?? []) as OrgLookupValue[];
    },
    [selectedAirline?.airline_id, category],
  );

  const createValue = useCallback(
    async (value: string, label?: string): Promise<OrgLookupValue> => {
      if (!selectedAirline?.airline_id) throw new Error('No airline selected');
      if (!user?.id) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('org_lookup_values')
        .upsert(
          {
            org_id: selectedAirline.airline_id,
            category,
            value,
            label: label ?? null,
            created_by: user.id,
          },
          { onConflict: 'org_id,category,value' },
        )
        .select('*')
        .single();

      if (error) throw error;
      return data as OrgLookupValue;
    },
    [selectedAirline?.airline_id, category, user?.id],
  );

  return { searchValues, createValue };
}
